import type { ToolPart } from "@opencode-ai/sdk/v2"
import type { UiI18nKey } from "@opencode-ai/ui/context/i18n"
import { businessToolLabel } from "@opencode-ai/session-ui/business-tool"
import { parseOperationProgress, type OperationProgress } from "./progress"

type ActivityDetailKey = keyof typeof import("@/i18n/en").dict
type ActivityLabelKey = UiI18nKey | ActivityDetailKey

const toolLabels: Record<string, ActivityLabelKey> = {
  skill: "cowork.timeline.activity.loadGuidance",
  read: "cowork.timeline.activity.reviewInformation",
  grep: "cowork.timeline.activity.searchInformation",
  glob: "cowork.timeline.activity.searchInformation",
  bash: "cowork.timeline.activity.processData",
  shell: "cowork.timeline.activity.processData",
  edit: "cowork.timeline.activity.prepareFiles",
  write: "cowork.timeline.activity.prepareFiles",
  andmera_run_get: "cowork.timeline.activity.loadRun",
  andmera_source_get: "cowork.timeline.activity.loadSource",
  andmera_report_registry: "cowork.timeline.activity.loadReportDefinition",
  andmera_knowledge_search: "cowork.timeline.activity.loadKnowledge",
  andmera_ledger_snapshot_reuse: "cowork.timeline.activity.checkHistory",
  andmera_financial_metrics: "cowork.timeline.activity.calculateMetrics",
  andmera_dynamic_report: "cowork.timeline.activity.prepareReport",
  andmera_event_normalize: "cowork.timeline.activity.normalizeEvidence",
  andmera_report_persist: "cowork.timeline.activity.saveReport",
  andmera_review_source: "cowork.timeline.activity.reviewInformation",
  andmera_review_candidate: "cowork.timeline.activity.reviewInformation",
}

const helperLabels: Record<string, ActivityLabelKey> = {
  "get-context": "cowork.timeline.activity.loadRun",
  "get-accounting-context": "cowork.timeline.activity.loadKnowledge",
  "list-files": "cowork.timeline.activity.searchInformation",
  "get-file": "cowork.timeline.activity.loadSource",
  "get-file-content": "cowork.timeline.activity.loadSource",
  "render-pdf-pages": "cowork.timeline.activity.reviewInformation",
  "list-knowledge": "cowork.timeline.activity.loadKnowledge",
  "get-knowledge-content": "cowork.timeline.activity.loadKnowledge",
  "list-extractions": "cowork.timeline.activity.checkHistory",
  "list-transformations": "cowork.timeline.activity.checkHistory",
  "save-extraction": "cowork.timeline.activity.prepareReport",
  "save-transformation": "cowork.timeline.activity.prepareReport",
  "save-artifact": "cowork.timeline.activity.saveReport",
}

const skillDetails: Record<string, ActivityDetailKey> = {
  "custom-report-workflow": "cowork.agents.template.customReport",
  "accounting-account-card": "cowork.agent.settings.template.accountCard.title",
  "accounting-ar-ap-analysis": "cowork.agent.settings.template.arAp.title",
  "accounting-balance-sheet-analysis": "cowork.agent.settings.template.balanceSheet.title",
  "accounting-cash-bank-analysis": "cowork.agent.settings.template.cashBank.title",
  "accounting-classification": "cowork.accounting.review.title",
  "accounting-coworker": "cowork.accounting.title",
  "accounting-data-model": "cowork.accounting.title",
  "accounting-document-structure-extraction": "cowork.accounting.documents.title",
  "accounting-document-understanding": "cowork.accounting.documents.title",
  "accounting-expense-classification": "cowork.accounting.review.title",
  "accounting-export": "cowork.accounting.exports.title",
  "accounting-generation-comparison": "cowork.usage.reports",
  "accounting-management-report": "cowork.agent.settings.template.management.title",
  "accounting-memory": "cowork.accounting.title",
  "accounting-payment-reconciliation": "cowork.accounting.payments.title",
  "accounting-pnl-analysis": "cowork.agent.settings.template.pnl.title",
  "accounting-posting-generation": "cowork.accounting.review.title",
  "accounting-reporting": "cowork.usage.reports",
  "accounting-trial-balance-analysis": "cowork.agent.settings.template.trialBalance.title",
  "accounting-validation": "cowork.enterprise.workflows.validation",
  "estonia-accounting": "cowork.accounting.title",
  "estonia-accounting-taxonomy": "cowork.accounting.title",
  "estonia-bilanss": "cowork.agent.settings.template.bilanss.title",
  "estonia-document-understanding": "cowork.accounting.documents.title",
  "estonia-expense-classification": "cowork.accounting.review.title",
  "estonia-fixed-assets": "cowork.accounting.title",
  "estonia-kaibeandmik": "cowork.agent.settings.template.trialBalance.title",
  "estonia-kasumiaruanne": "cowork.agent.settings.template.kasumiaruanne.title",
  "estonia-paevaaramat": "cowork.agent.settings.template.journal.title",
  "estonia-payroll": "cowork.agent.settings.template.tsd.title",
  "estonia-pnl-classification": "cowork.agent.settings.template.pnl.title",
  "estonia-tax-account-mapping": "cowork.accounting.tax.title",
  "estonia-vat": "cowork.agent.settings.template.kmd.title",
}

const helperDetails: Record<string, ActivityDetailKey> = {
  "get-context": "cowork.enterprise.scope.project",
  "get-accounting-context": "cowork.accounting.title",
  "resolve-extraction-plan": "cowork.usage.extractions",
  "get-source-extraction": "cowork.usage.extractions",
  "save-source-extraction": "cowork.usage.extractions",
  "finalize-extraction-set": "cowork.usage.extractions",
  "normalize-accounting-events": "cowork.accounting.title",
  "get-accounting-event-set": "cowork.accounting.title",
  "resolve-knowledge-extraction-plan": "cowork.enterprise.knowledge.title",
  "save-knowledge-source-extraction": "cowork.enterprise.knowledge.title",
  "save-accounting-profile-knowledge": "cowork.accounting.title",
  "list-files": "cowork.usage.files",
  "get-file": "cowork.usage.files",
  "get-file-content": "cowork.usage.files",
  "render-pdf-pages": "cowork.usage.files",
  "list-knowledge": "cowork.enterprise.knowledge.title",
  "get-knowledge-content": "cowork.enterprise.knowledge.title",
  "list-extractions": "cowork.usage.extractions",
  "save-extraction": "cowork.usage.extractions",
  "list-transformations": "cowork.usage.transformations",
  "save-transformation": "cowork.usage.transformations",
  "save-accounting-document-analysis": "cowork.accounting.documents.title",
  "save-accounting-posting-suggestion": "cowork.accounting.review.title",
  "save-accounting-review-item": "cowork.accounting.review.title",
  "save-accounting-payment": "cowork.accounting.payments.title",
  "save-accounting-payment-match": "cowork.accounting.payments.title",
  "save-accounting-tax-report": "cowork.accounting.tax.title",
  "list-artifacts": "cowork.usage.reports",
  "save-artifact": "cowork.usage.reports",
  "delete-artifact": "cowork.usage.reports",
}

const nativeToolDetails: Record<string, ActivityDetailKey> = {
  andmera_run_get: "cowork.enterprise.scope.project",
  andmera_source_get: "cowork.usage.files",
  andmera_report_registry: "cowork.usage.reports",
  andmera_knowledge_search: "cowork.enterprise.knowledge.title",
  andmera_ledger_snapshot_reuse: "cowork.accounting.title",
  andmera_financial_metrics: "cowork.usage.reports",
  andmera_dynamic_report: "cowork.usage.reports",
  andmera_event_normalize: "cowork.accounting.title",
  andmera_report_persist: "cowork.usage.reports",
  andmera_review_source: "cowork.usage.files",
  andmera_review_candidate: "cowork.usage.reports",
}

export type BusinessActivity = {
  id: string
  label: ActivityLabelKey
  status: ToolPart["state"]["status"]
  filename?: string
  detailKey?: ActivityDetailKey
  progress?: OperationProgress
}

export function businessActivities(part: ToolPart): BusinessActivity[] {
  if (["question", "task"].includes(part.tool)) return []
  const metadata = "metadata" in part.state ? part.state.metadata : undefined
  let progress: unknown = metadata?.progress
  if (
    part.state.status === "completed" &&
    part.state.output.length <= 262_144 &&
    part.state.output.includes('"andmera.operation_progress"')
  ) {
    try {
      const result = JSON.parse(part.state.output)
      if (result && typeof result === "object" && !Array.isArray(result) && result.progress !== undefined)
        progress = result.progress
    } catch {
      // Legacy tool output remains eligible for the existing generic projection.
    }
  }
  const values = Array.isArray(progress) ? progress : [progress]
  const activities = values.flatMap((value): BusinessActivity[] => {
    const event = parseOperationProgress(value)
    if (!event) return []
    return [
      {
        id: `${part.sessionID}:progress:${event.operation_id}`,
        label: "ui.tool.processingData",
        status: event.status === "failed" ? "error" : event.status,
        progress: event,
      },
    ]
  })
  if (activities.length) return activities
  const legacy = businessActivity(part)
  return legacy
    ? [legacy]
    : [{ id: `${part.messageID}:${part.id}`, label: "ui.tool.processingData", status: part.state.status }]
}

const documentExtension = /\.(pdf|xlsx?|xlsm|csv|ods|docx?|odt|pptx?|png|jpe?g|tiff?)$/i

function safeDocumentName(path: unknown) {
  if (typeof path !== "string" || path.length > 2048 || /[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/.test(path))
    return
  const segments = path.replace(/\\/g, "/").split("/")
  const name = segments.at(-1) ?? ""
  if (
    name.length > 160 ||
    !documentExtension.test(name) ||
    /^page[-_]\d+\.(png|jpe?g|tiff?)$/i.test(name) ||
    segments.some((segment) => segment.startsWith(".") && segment !== "." && segment !== "..") ||
    /tool-output|node_modules|credentials?|secrets?|password|private[-_]key|visual-attempt/i.test(path) ||
    /^[a-z]+:\/\//i.test(path) ||
    /[<>?]/.test(name) ||
    /^[\da-f-]{32,}\./i.test(name)
  )
    return
  return name
}

function helperAction(part: ToolPart) {
  if (!["bash", "shell"].includes(part.tool)) return
  const command = part.state.input.command
  if (typeof command !== "string" || command.length > 16_384) return
  return command.match(/cowork_agent_api\.py\s+([a-z-]+)\b/)?.[1]
}

function helperDocumentName(part: ToolPart, action: string | undefined) {
  if (
    part.state.status !== "completed" ||
    !action ||
    !["get-file", "get-file-content", "render-pdf-pages"].includes(action) ||
    part.state.output.length > 262_144
  )
    return
  try {
    const result = JSON.parse(part.state.output)
    return safeDocumentName(result?.filename ?? result?.file_name)
  } catch {
    return
  }
}

function nativeDocumentName(part: ToolPart) {
  if (part.tool !== "andmera_source_get" || part.state.status !== "completed" || part.state.output.length > 262_144)
    return
  try {
    return safeDocumentName(JSON.parse(part.state.output)?.filename)
  } catch {
    return
  }
}

export function businessActivity(part: ToolPart): BusinessActivity | undefined {
  const nativeDetailKey = nativeToolDetails[part.tool]
  const skill = part.tool === "skill" ? part.state.input.name : undefined
  const action = helperAction(part)
  const label = (action ? helperLabels[action] : undefined) ?? toolLabels[part.tool] ?? businessToolLabel(part.tool, true)
  if (!label) return
  const detailKey = typeof skill === "string" ? skillDetails[skill] : action ? helperDetails[action] : nativeDetailKey
  let filename = helperDocumentName(part, action) ?? nativeDocumentName(part)
  if (["read", "edit", "write"].includes(part.tool)) {
    const path = part.state.input.filePath ?? part.state.input.path
    filename = safeDocumentName(path)
  }
  return {
    id: `${part.messageID}:${part.id}`,
    label,
    status: part.state.status,
    ...(filename ? { filename } : {}),
    ...(detailKey ? { detailKey } : {}),
  }
}
